// Payment System v3.0 PR 7 — multi-investor closing wizard (design spec
// §8.2). Route-driven page (not a modal) at
// /capital-markets/pools/:id/closings/new. Three steps:
//   1. Pre-flight — verify pool status and surface fill / TPA context
//   2. Subscriptions — editable table of (investor, tier, amount) rows
//   3. Preview — server-computed cascade preview + Confirm button that
//      POSTs to the execute endpoint and redirects to the detail page
//
// Adapted from InviteInvestorWizard's stepper / form pattern.

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Plus, Trash2 } from "lucide-react";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { WaterfallBlockedNotice } from "@/components/payments/WaterfallBlockedNotice";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { cn } from "@/design-system/lib/utils";
import {
  useExecuteClosing,
  usePreviewClosing,
  useSpvPool,
  useSpvPoolMath,
} from "@/hooks/capitalMarkets/useSpvPools";
import { useInvestorList } from "@/hooks/capitalMarkets/useInvestors";
import {
  resolveInvestorName,
  useEntityNameMap,
  usePersonNameMap,
} from "@/hooks/capitalMarkets/useCounterpartyNames";
import type { InvestorMaskedDto } from "@/types/investors";
import type {
  ClosingPreview,
  ClosingTier,
  SpvPoolStatus,
} from "@/types/spvPool";
import { CLOSING_TIERS } from "@/types/spvPool";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";
import { formatCurrency, formatPct } from "../shared/format";

type Step = 1 | 2 | 3;

interface SubscriptionRow {
  id: string; // local UI id
  investorId: string;
  tier: ClosingTier;
  amount: string; // raw input string for stable typing
}

const TIER_LABEL: Record<ClosingTier, string> = {
  tier_a: "Tier A",
  tier_b: "Tier B",
  tier_c1: "Tier C1",
};

const ALLOWED_STATUSES: readonly SpvPoolStatus[] = [
  "InvestorCommitment",
  "Closing",
  "Active",
];

function newRow(): SubscriptionRow {
  return {
    id: crypto.randomUUID(),
    investorId: "",
    tier: "tier_a",
    amount: "",
  };
}

export function InvestorClosingWizard() {
  const { id: poolId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const tz = useUserTimeZone();

  const poolQuery = useSpvPool(poolId);
  const mathQuery = useSpvPoolMath(poolId);
  const investorsQuery = useInvestorList({ status: "Active", pageSize: 200 });
  const { map: personNames } = usePersonNameMap();
  const { map: entityNames } = useEntityNameMap();

  const preview = usePreviewClosing(poolId ?? "");
  const execute = useExecuteClosing(poolId ?? "");

  const [step, setStep] = useState<Step>(1);
  const [closingDate, setClosingDate] = useState(
    () => isoDateInTz(new Date(), tz),
  );
  const [rows, setRows] = useState<SubscriptionRow[]>([newRow()]);
  const [previewData, setPreviewData] = useState<ClosingPreview | null>(null);

  const pool = poolQuery.data;
  const math = mathQuery.data;
  const investors = investorsQuery.data?.data ?? [];

  const nameOf = (inv: InvestorMaskedDto) =>
    resolveInvestorName(inv.personId, inv.entityId, personNames, entityNames);

  const grossProceeds = useMemo(() => {
    return rows.reduce((sum, r) => {
      const n = Number(r.amount);
      return Number.isFinite(n) && n > 0 ? sum + n : sum;
    }, 0);
  }, [rows]);

  const cumulativeBefore = useMemo(() => {
    // Backend tracks CumulativeInvestorCapital on SPVPool; the math endpoint
    // exposes ContributedPrincipal which is the contributed-loan total. We
    // surface the contributed principal here as a proxy for "where the
    // cascade sits today"; the server returns the authoritative number on
    // preview/execute so this is purely informational.
    return math?.contributedPrincipal ?? 0;
  }, [math]);

  const cumulativeAfter = cumulativeBefore + grossProceeds;
  const targetSize = pool?.targetPoolSize ?? 0;
  const fillAfterPct =
    targetSize > 0 ? Math.min(1, cumulativeAfter / targetSize) : 0;

  const statusAllowed =
    pool !== undefined && ALLOWED_STATUSES.includes(pool.status);

  const rowsValid =
    rows.length > 0 &&
    rows.every((r) => {
      const n = Number(r.amount);
      return (
        r.investorId.length > 0 &&
        CLOSING_TIERS.includes(r.tier) &&
        Number.isFinite(n) &&
        n > 0
      );
    });

  const handleAddRow = () => setRows((rs) => [...rs, newRow()]);
  const handleRemoveRow = (id: string) =>
    setRows((rs) => (rs.length === 1 ? rs : rs.filter((r) => r.id !== id)));

  const updateRow = (id: string, patch: Partial<SubscriptionRow>) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleRunPreview = () => {
    if (!poolId) return;
    preview
      .mutateAsync({
        closingDate,
        subscriptions: rows.map((r) => ({
          investorId: r.investorId,
          tier: r.tier,
          amount: Number(r.amount),
        })),
      })
      .then((data) => {
        setPreviewData(data);
        setStep(3);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleConfirm = () => {
    if (!poolId) return;
    // The pool DTO doesn't expose VersionId on the wire; the backend
    // accepts the current pool version through the same path as
    // FinalizePool. We pass 0 to let the server validate against its
    // own current value; if a 409 comes back the toast surfaces the
    // optimistic-concurrency mismatch and the operator re-runs preview.
    const expectedVersionId = 0;
    execute
      .mutateAsync({
        closingDate,
        expectedVersionId,
        subscriptions: rows.map((r) => ({
          investorId: r.investorId,
          tier: r.tier,
          amount: Number(r.amount),
        })),
      })
      .then((res) => {
        toast.success(
          `Closing recorded — ${res.ssnsIssuedCount} SSN${
            res.ssnsIssuedCount === 1 ? "" : "s"
          } issued.`,
        );
        navigate(
          `/capital-markets/pools/${poolId}?tab=closings&highlight=${res.paymentId}`,
        );
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  if (poolQuery.isLoading || !pool) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading pool…</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center gap-space-2">
        <button
          type="button"
          onClick={() => navigate(`/capital-markets/pools/${pool.id}?tab=closings`)}
          className="text-gold-shadow hover:underline text-small flex items-center gap-space-1"
        >
          <ArrowLeft size={14} aria-hidden /> Back to pool
        </button>
      </div>
      <div className="flex flex-col gap-space-1">
        <h1 className="font-display font-bold text-h2 text-onyx m-0">
          New Closing — {pool.poolName}
        </h1>
        <p className="text-small text-gray-mid m-0">
          Record a multi-investor closing. The cascade is server-computed; the
          wizard surfaces the preview before any writes.
        </p>
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <Card>
          <h3 className="font-display font-bold text-h3 m-0">Pre-flight</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 mt-space-3 text-small">
            <Item label="Pool" value={pool.poolName} />
            <Item label="Status" value={pool.status} />
            <Item
              label="Target pool size (TPA)"
              value={formatCurrency(pool.targetPoolSize)}
            />
            <Item
              label="Contributed principal"
              value={formatCurrency(math?.contributedPrincipal)}
            />
            <Item
              label="Investment fill %"
              value={formatPct(math?.investmentFillPct)}
            />
            <Item
              label="k range (segments)"
              value={
                math
                  ? `${math.contributedSegmentCount} / ${math.eligibleSegmentTotal}`
                  : "—"
              }
            />
          </dl>
          {!statusAllowed && (
            <div className="mt-space-3 rounded-md border border-status-error/30 bg-status-error/10 p-space-3 text-small">
              Pool status is <strong>{pool.status}</strong>. Closings can only
              be run on pools in <code>InvestorCommitment</code>,{" "}
              <code>Closing</code>, or <code>Active</code>.
            </div>
          )}
          <div className="mt-space-4 flex justify-end">
            <Button
              size="sm"
              onClick={() => setStep(2)}
              disabled={!statusAllowed}
            >
              Next
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-h3 m-0">
              Subscriptions
            </h3>
            <FormField label="Closing date">
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                className="w-44"
                aria-label="Closing date"
              />
            </FormField>
          </div>

          <div className="mt-space-3 overflow-x-auto">
            <table className="w-full text-small">
              <thead>
                <tr className="text-left text-gold uppercase text-[10px] font-display tracking-label">
                  <th className="py-space-2 pr-space-2 w-1/2">Investor</th>
                  <th className="py-space-2 pr-space-2">Tier</th>
                  <th className="py-space-2 pr-space-2 text-right">Amount</th>
                  <th className="py-space-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-border-light align-top"
                  >
                    <td className="py-space-2 pr-space-2">
                      <Select
                        aria-label="Investor"
                        value={row.investorId}
                        onChange={(e) =>
                          updateRow(row.id, { investorId: e.target.value })
                        }
                      >
                        <option value="">Select an investor…</option>
                        {investors.map((inv) => (
                          <option key={inv.id} value={inv.id}>
                            {nameOf(inv)}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-space-2 pr-space-2">
                      <Select
                        aria-label="Tier"
                        value={row.tier}
                        onChange={(e) =>
                          updateRow(row.id, {
                            tier: e.target.value as ClosingTier,
                          })
                        }
                      >
                        {CLOSING_TIERS.map((t) => (
                          <option key={t} value={t}>
                            {TIER_LABEL[t]}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-space-2 pr-space-2">
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        value={row.amount}
                        onChange={(e) =>
                          updateRow(row.id, { amount: e.target.value })
                        }
                        placeholder="0"
                        className="text-right"
                        aria-label="Amount"
                      />
                    </td>
                    <td className="py-space-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(row.id)}
                        disabled={rows.length === 1}
                        className="text-gray-mid hover:text-status-error disabled:opacity-30"
                        aria-label="Remove subscription"
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-space-3 flex items-center justify-between">
            <Button variant="secondary" size="sm" onClick={handleAddRow}>
              <Plus size={14} aria-hidden /> Add row
            </Button>
            <div className="flex flex-col items-end gap-space-1 text-small">
              <div>
                <span className="text-gray-mid">Gross proceeds: </span>
                <strong>{formatCurrency(grossProceeds)}</strong>
              </div>
              <div className="text-caption text-gray-mid">
                Cumulative after: {formatCurrency(cumulativeAfter)} ·{" "}
                Fill after: {formatPct(fillAfterPct)}
              </div>
            </div>
          </div>

          <div className="mt-space-4 flex justify-end gap-space-2">
            <Button variant="secondary" size="sm" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              size="sm"
              loading={preview.isPending}
              disabled={!rowsValid || !closingDate}
              onClick={handleRunPreview}
            >
              Preview cascade
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && previewData && (
        <div className="flex flex-col gap-space-3">
          <Card>
            <h3 className="font-display font-bold text-h3 m-0">
              Closing preview
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3 mt-space-3 text-small">
              <Item
                label="Gross proceeds"
                value={formatCurrency(previewData.grossProceeds)}
              />
              <Item
                label="Cumulative before"
                value={formatCurrency(
                  previewData.cumulativeInvestorCapitalBefore,
                )}
              />
              <Item
                label="Cumulative after"
                value={formatCurrency(
                  previewData.cumulativeInvestorCapitalAfter,
                )}
              />
              <Item
                label="Fill % after"
                value={formatPct(previewData.fillPercentageAfter)}
              />
              <Item
                label="RUF firings"
                value={previewData.rufFiringCount.toString()}
              />
              <Item
                label="SFTF firings"
                value={previewData.sftfFiringCount.toString()}
              />
            </div>
            <div className="mt-space-3 flex flex-wrap gap-space-2">
              <span className="text-caption uppercase text-gray-mid tracking-label">
                Newly triggered segments:
              </span>
              {previewData.newlyTriggeredSegments.length === 0 ? (
                <span className="text-caption text-gray-mid italic">none</span>
              ) : (
                previewData.newlyTriggeredSegments.map((s) => (
                  <Badge key={s} status="gold">
                    S{s}
                  </Badge>
                ))
              )}
            </div>
          </Card>

          <Card>
            <h4 className="font-display font-bold text-onyx m-0">
              SSN issuances ({previewData.ssnIssuances.length})
            </h4>
            {previewData.ssnIssuances.length === 0 ? (
              <p className="text-small text-gray-mid italic mt-space-2 m-0">
                No SSNs would be issued at this closing.
              </p>
            ) : (
              <div className="mt-space-3">
                <Table>
                  <THead>
                    <TR>
                      <TH>Loan</TH>
                      <TH>Segment</TH>
                      <TH className="text-right">Face Amount</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {previewData.ssnIssuances.map((ssn) => (
                      <TR key={`${ssn.loanId}-${ssn.segmentNumber}`}>
                        <TD>{ssn.loanNumber}</TD>
                        <TD>S{ssn.segmentNumber}</TD>
                        <TD className="text-right">
                          {formatCurrency(ssn.ssnFaceAmount)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
          </Card>

          <Card>
            <h4 className="font-display font-bold text-onyx m-0">
              {previewData.wf2BlockedReason
                ? "WF2 disbursements"
                : `WF2 disbursements (${previewData.wf2Disbursements.length})`}
            </h4>
            {previewData.wf2BlockedReason ? (
              <div className="mt-space-3">
                <WaterfallBlockedNotice
                  reason={previewData.wf2BlockedReason}
                  subject="closing"
                  scopeNote="Everything above is unaffected — only the waterfall split is blocked."
                />
              </div>
            ) : previewData.wf2Disbursements.length === 0 ? (
              <p className="text-small text-gray-mid italic mt-space-2 m-0">
                No disbursements at this closing.
              </p>
            ) : (
              <div className="mt-space-3">
                <Table>
                  <THead>
                    <TR>
                      <TH>Priority</TH>
                      <TH>Recipient</TH>
                      <TH>Obligation</TH>
                      <TH className="text-right">Amount</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {previewData.wf2Disbursements.map((d, i) => (
                      <TR key={`${d.priorityPosition}-${i}`}>
                        <TD>P{d.priorityPosition}</TD>
                        <TD>
                          {d.recipientDisplayName ?? d.recipientResolution}
                        </TD>
                        <TD>
                          <code className="text-caption">
                            {d.obligationTypeCode}
                          </code>
                        </TD>
                        <TD className="text-right">
                          {formatCurrency(d.amount)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
          </Card>

          <div className="flex items-center justify-end gap-space-3">
            {previewData.wf2BlockedReason && (
              // Says why the button is dead, next to the button — the reason
              // itself is up in the WF2 card and may be scrolled off.
              <p className="m-0 text-small text-gray-mid">
                Blocked by the waterfall split above.
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPreviewData(null);
                setStep(2);
              }}
            >
              Back
            </Button>
            <Button
              size="sm"
              loading={execute.isPending}
              disabled={Boolean(previewData.wf2BlockedReason)}
              onClick={handleConfirm}
            >
              Confirm Closing
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Item({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-label uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="m-0 text-onyx font-semibold">{value}</dd>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ["Pre-flight", "Subscriptions", "Preview"];
  return (
    <ol className="flex items-center gap-space-2 m-0 p-0 list-none">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const done = step > n;
        const active = step === n;
        return (
          <li key={label} className="flex items-center gap-space-2">
            <span
              className={cn(
                "h-6 w-6 rounded-full flex items-center justify-center text-caption font-bold",
                done && "bg-status-success text-white-pure",
                active && "bg-gold text-onyx",
                !done && !active && "bg-white-warm text-gray-mid",
              )}
            >
              {done ? <Check size={12} aria-hidden /> : n}
            </span>
            <span
              className={cn(
                "text-caption font-display uppercase tracking-wide",
                active ? "text-onyx" : "text-gray-mid",
              )}
            >
              {label}
            </span>
            {i < labels.length - 1 && (
              <span className="text-gray-mid">→</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
